import { apply, compose, leafBox, multiply, } from './bounds.js';
import { modelScale, modelSlugOf, modelSweptBox, } from './models.js';
import { POSTER_LIMITS, posterLayout } from './posters.js';
const sub = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const cross = (a, b) => [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
];
const unit = (v) => {
    const n = Math.hypot(v[0], v[1], v[2]);
    return n < 1e-9 ? undefined : [v[0] / n, v[1] / n, v[2] / n];
};
/** Adds a direction unless it, or its opposite, is there already. */
function addDirection(list, v) {
    if (!v)
        return;
    if (list.some((d) => Math.abs(Math.abs(dot(d, v)) - 1) < 1e-9))
        return;
    list.push(v);
}
/** A convex solid from its corners in place and its faces as rings of corner indices. */
function convex(points, faces) {
    const axes = [];
    const edges = [];
    for (const face of faces) {
        const ring = face.map((i) => points[i]);
        let normal;
        for (let k = 1; k + 1 < ring.length && !normal; k++) {
            normal = unit(cross(sub(ring[k], ring[0]), sub(ring[k + 1], ring[0])));
        }
        addDirection(axes, normal);
        for (let k = 0; k < ring.length; k++) {
            const edge = unit(sub(ring[(k + 1) % ring.length], ring[k]));
            addDirection(edges, edge);
            // A flat solid's sides are its edges' normals in its plane.
            if (normal && edge)
                addDirection(axes, unit(cross(normal, edge)));
        }
    }
    return { kind: 'convex', points, axes, edges };
}
/** A box's eight corners in bounds.ts's order (x: 4, y: 2, z: 1), and its six faces. */
const BOX_FACES = [
    [0, 1, 3, 2],
    [4, 6, 7, 5],
    [0, 4, 5, 1],
    [2, 3, 7, 6],
    [0, 2, 6, 4],
    [1, 5, 7, 3],
];
function boxSolid(box, place) {
    const points = [];
    for (const x of [box.min[0], box.max[0]]) {
        for (const y of [box.min[1], box.max[1]]) {
            for (const z of [box.min[2], box.max[2]])
                points.push(apply(place, [x, y, z]));
        }
    }
    return convex(points, BOX_FACES);
}
/** Sides of the prism round a round part. */
const SIDES = 8;
/**
 * Corners at 22.5° + k·45°, out to r / cos 22.5°: the eight sides touch the
 * circle, so the prism holds the round part, and its corners stay within the
 * part's square box.
 */
const OUT = 1 / Math.cos(Math.PI / SIDES);
/** An eight-sided frustum round y, radius `r0` at `y0` and `r1` at `y1`. */
function frustum(r0, y0, r1, y1, place) {
    const points = [];
    for (const [r, y] of [
        [r0, y0],
        [r1, y1],
    ]) {
        for (let k = 0; k < SIDES; k++) {
            const angle = ((k + 0.5) * 2 * Math.PI) / SIDES;
            points.push(apply(place, [r * OUT * Math.cos(angle), y, r * OUT * Math.sin(angle)]));
        }
    }
    const bottom = Array.from({ length: SIDES }, (_, k) => k);
    const top = bottom.map((k) => k + SIDES);
    const sides = bottom.map((k) => [k, (k + 1) % SIDES, ((k + 1) % SIDES) + SIDES, k + SIDES]);
    return convex(points, [bottom, top, ...sides]);
}
/** The outline's convex hull, anticlockwise (Andrew's monotone chain). */
function hull2(points) {
    const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
    if (sorted.length < 3)
        return sorted.map(([x, y]) => [x, y]);
    const turn = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
    const chain = (list) => {
        const out = [];
        for (const p of list) {
            while (out.length >= 2 && turn(out[out.length - 2], out[out.length - 1], p) <= 0)
                out.pop();
            out.push([p[0], p[1]]);
        }
        return out;
    };
    const lower = chain(sorted);
    const upper = chain([...sorted].reverse());
    return [...lower.slice(0, -1), ...upper.slice(0, -1)];
}
/** How much a placement scales, alike in every direction (bounds.ts's compose). */
const scaleOf = (place) => Math.hypot(place[0], place[4], place[8]);
function leafSolids(part, place) {
    switch (part.type) {
        case 'sphere':
            return [
                { kind: 'ball', centre: apply(place, [0, 0, 0]), radius: part.size[0] * scaleOf(place) },
            ];
        case 'cylinder':
            // three.js's CylinderGeometry: top radius, bottom radius, height.
            return [frustum(part.size[1], -part.size[2] / 2, part.size[0], part.size[2] / 2, place)];
        case 'cone':
            return [frustum(part.size[0], -part.size[1] / 2, 0, part.size[1] / 2, place)];
        case 'capsule': {
            const [r, length] = part.size;
            const radius = r * scaleOf(place);
            return [
                frustum(r, -length / 2, r, length / 2, place),
                { kind: 'ball', centre: apply(place, [0, -length / 2, 0]), radius },
                { kind: 'ball', centre: apply(place, [0, length / 2, 0]), radius },
            ];
        }
        case 'lathe': {
            const points = part.points.map(([r, y]) => [Math.max(0, r), y]);
            const solids = [];
            for (let i = 0; i + 1 < points.length; i++) {
                const [r0, y0] = points[i];
                const [r1, y1] = points[i + 1];
                solids.push(frustum(r0, y0, r1, y1, place));
            }
            return solids.length > 0 ? solids : [boxSolid(leafBox(part), place)];
        }
        case 'extrude': {
            const outline = hull2(part.outline);
            if (outline.length < 3)
                return [boxSolid(leafBox(part), place)];
            const half = part.depth / 2;
            const points = [
                ...outline.map(([x, y]) => apply(place, [x, y, -half])),
                ...outline.map(([x, y]) => apply(place, [x, y, half])),
            ];
            const n = outline.length;
            const back = Array.from({ length: n }, (_, k) => k);
            const front = back.map((k) => k + n);
            const sides = back.map((k) => [k, (k + 1) % n, ((k + 1) % n) + n, k + n]);
            return [convex(points, [back, front, ...sides])];
        }
        default:
            // Boxes, planes and tori: their box.
            return [boxSolid(leafBox(part), place)];
    }
}
function collectSolids(parts, parent, out) {
    for (const part of parts) {
        if (part.type === 'group') {
            collectSolids(part.parts, multiply(parent, compose(part.pos, part.rot, part.scale)), out);
            continue;
        }
        out.push(...leafSolids(part, multiply(parent, compose(part.pos, part.rot))));
    }
}
/**
 * The solids a model is made of in a given state, or undefined when its
 * bounds say all there is: hidden, a planet or star (already a sphere), an
 * orbit or ring (a disc), or a bone.
 */
export function objectSolids(object, state, catalogue, clips) {
    if (!state.visible)
        return undefined;
    const [namespace = ''] = object.kind.split(':');
    if (namespace === 'model') {
        // A library model (ADR-043) as its box, swept by the clips it plays, turned
        // with it: a T. rex side-on is not the axis-aligned box around it.
        const slug = modelSlugOf(object.kind);
        const facts = slug === undefined ? undefined : catalogue?.[slug];
        if (!facts)
            return undefined;
        const scale = modelScale(object, facts) * state.scale;
        return [boxSolid(modelSweptBox(facts, clips), compose(state.pos, [0, state.rot[1], 0], scale))];
    }
    const place = compose(state.pos, state.rot, state.scale);
    if (namespace === 'shape') {
        const solids = [];
        collectSolids(object.parts ?? [], place, solids);
        // No parts: the headset draws a 0.1 m grey block (render/shapes.ts).
        return solids.length > 0
            ? solids
            : [boxSolid({ min: [-0.05, -0.05, -0.05], max: [0.05, 0.05, 0.05] }, place)];
    }
    if (namespace === 'poster') {
        const { outer } = posterLayout(object);
        // Turned by its facing only, as bounds.ts frames it.
        const frame = compose(state.pos, state.rot);
        return [
            boxSolid({
                min: [-outer.width / 2, -outer.height / 2, -POSTER_LIMITS.depth / 2],
                max: [outer.width / 2, outer.height / 2, POSTER_LIMITS.depth / 2],
            }, frame),
        ];
    }
    return undefined;
}
/**
 * Whether a point is in a solid (touching counts). A part with no width -- a
 * lathe drawn on its own axis -- has no face to be inside of, and contains
 * nothing.
 */
export function contains(solid, p) {
    if (solid.kind === 'ball') {
        const d = sub(p, solid.centre);
        return Math.sqrt(dot(d, d)) <= solid.radius + 1e-6;
    }
    if (solid.axes.length === 0)
        return false;
    return solid.axes.every((axis) => {
        const along = dot(p, axis);
        let min = Infinity;
        let max = -Infinity;
        for (const q of solid.points) {
            const t = dot(q, axis);
            min = Math.min(min, t);
            max = Math.max(max, t);
        }
        return along >= min - 1e-6 && along <= max + 1e-6;
    });
}
/** Whether a solid and an oriented box touch (separating axes; a ball by its nearest point). */
export function solidTouches(solid, box) {
    if (solid.kind === 'ball') {
        const d = sub(solid.centre, box.centre);
        let squared = 0;
        for (let i = 0; i < 3; i++) {
            const outside = Math.abs(dot(d, box.axes[i])) - box.half[i];
            if (outside > 0)
                squared += outside * outside;
        }
        return squared <= solid.radius * solid.radius;
    }
    const reach = (axis) => box.half[0] * Math.abs(dot(box.axes[0], axis)) +
        box.half[1] * Math.abs(dot(box.axes[1], axis)) +
        box.half[2] * Math.abs(dot(box.axes[2], axis));
    const parted = (axis) => {
        if (dot(axis, axis) < 1e-12)
            return false;
        let min = Infinity;
        let max = -Infinity;
        for (const q of solid.points) {
            const t = dot(q, axis);
            min = Math.min(min, t);
            max = Math.max(max, t);
        }
        const middle = dot(box.centre, axis);
        const r = reach(axis);
        return middle + r < min - 1e-12 || middle - r > max + 1e-12;
    };
    if (box.axes.some(parted) || solid.axes.some(parted))
        return false;
    for (const edge of solid.edges) {
        for (const axis of box.axes)
            if (parted(cross(edge, axis)))
                return false;
    }
    return true;
}
//# sourceMappingURL=solids.js.map